import { ethers } from "ethers";

/**
 * Everything that knows about the chain, in one place.
 *
 * The RPC is reached through `/rpc` on this app's own origin rather than at
 * `127.0.0.1:8545` directly. Besu's port stays bound to loopback on the host —
 * nginx is the only thing that can reach it, and the only thing the public can
 * reach. That keeps the chain's port closed while still letting a browser use
 * it, and it means a rate limit or an allowlist has one place to live.
 *
 * Worth being explicit, because it inverts something the chain design says:
 * the production design's confidentiality rests partly on the RPC being
 * unreachable ("privacy comes first from unreachability"). A devnet that
 * nobody can reach is useless, so this deployment deliberately gives that up.
 * It is a devnet property and must not be carried into production.
 */
export const RPC_URL = `${window.location.origin}/rpc`;

export interface Manifest {
  chainId: number;
  network: string;
  deployedAt: string;
  deployer: string;
  contracts: {
    token: string;
    escrow: string;
    issuer: string;
    adminRegistry: string;
    dispenser: string;
  };
  dispenser: { nativePerDrip: string; tokensPerDrip: string; cooldownSeconds: number };
  token: { symbol: string; decimals: number };
}

let cached: Manifest | undefined;

/**
 * Read fresh on each call rather than baking it into the bundle. A redeploy
 * changes every address, and a bundle built against the old ones agrees with
 * itself perfectly while pointing at contracts that no longer exist — the
 * failure looks like an empty chain rather than a stale build.
 */
export async function manifest(): Promise<Manifest> {
  if (cached) return cached;
  const res = await fetch("/devnet.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`no devnet manifest (${res.status}) — has deploy-devnet been run?`);
  cached = (await res.json()) as Manifest;
  return cached;
}

let providerInstance: ethers.JsonRpcProvider | undefined;

export function provider(): ethers.JsonRpcProvider {
  if (!providerInstance) {
    providerInstance = new ethers.JsonRpcProvider(RPC_URL, undefined, {
      // The chain never changes under this app, and letting ethers re-detect
      // it on every call is a round trip per call for no benefit.
      staticNetwork: true,
    });
    // `pollingInterval` is a property, NOT a constructor option -- passing it
    // above is silently ignored, which is how a chain that mines in two
    // seconds ended up taking about forty seconds per confirmation here. The
    // block time is two seconds, so poll a little under it.
    providerInstance.pollingInterval = 1000;
  }
  return providerInstance;
}

/**
 * Re-bind a contract to a different signer, keeping its callable type.
 *
 * `Contract.connect()` is declared as returning `BaseContract` in ethers v6.
 * The object it returns at runtime still carries the method proxy, but the
 * type no longer does, so `escrow.connect(x).create(...)` fails to compile
 * even though it works. This narrows the result back to what it actually is,
 * in one place, instead of a cast at every call site.
 */
export function withSigner(c: ethers.Contract, runner: ethers.ContractRunner): ethers.Contract {
  return c.connect(runner) as ethers.Contract;
}

export const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function totalSupply() view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function approve(address spender, uint256 amount) returns (bool)",
];

export const DISPENSER_ABI = [
  "function drip(address to)",
  "function available(address to) view returns (uint256 native, uint256 tokens, uint256 readyAt)",
  "function cooldown() view returns (uint256)",
  "event Dispensed(address indexed to, uint256 native, uint256 tokens)",
];

export const ESCROW_ABI = [
  // Writes. The SettlX flow drives the whole lifecycle, so the read-only
  // subset an explorer needs is not enough here.
  "function create(bytes32 dealId, address buyer, address seller, address platform, address token, uint256 total, uint64 deadline, bytes32 contentHash, bytes32[3] commitments, bytes content)",
  "function fund(bytes32 dealId)",
  "function activate(bytes32 dealId, uint256 nonce, bytes[] sigs)",
  "function release(bytes32 dealId, address to, uint256 amount, uint256 nonce, bytes[] sigs)",
  "function dispute(bytes32 dealId, uint256 nonce, bytes sig)",
  "function attestShareDestroyed(bytes32 dealId, uint256 nonce, bytes sig)",
  "function getDeal(bytes32 dealId) view returns (tuple(address buyer,address seller,address platform,address token,uint256 total,uint256 released,uint64 deadline,uint8 state,bytes32 contentHash))",
  "function shareCommitments(bytes32 dealId) view returns (bytes32[3])",
  "event DealCreated(bytes32 indexed dealId, address buyer, address seller, address platform, address token, uint256 total, uint64 deadline, bytes32 contentHash)",
  "event DealContent(bytes32 indexed dealId, bytes content)",
  "event Funded(bytes32 indexed dealId, uint256 amount)",
  "event Activated(bytes32 indexed dealId)",
  "event Released(bytes32 indexed dealId, address to, uint256 amount)",
  "event Settled(bytes32 indexed dealId)",
  "event Disputed(bytes32 indexed dealId, address by)",
  "event ShareDestroyed(bytes32 indexed dealId, address holder, uint256 blockNumber)",
  "event Shredded(bytes32 indexed dealId)",
];

/** The escrow's own enum, in its own order. */
export const DEAL_STATES = [
  "None", "Created", "Funded", "Active", "Disputed", "Settled", "Refunded", "Shredded",
] as const;

export function shortHex(v: string, lead = 6, tail = 4): string {
  return v.length <= lead + tail + 2 ? v : `${v.slice(0, lead + 2)}…${v.slice(-tail)}`;
}

export function fmtUnits(v: bigint, decimals: number, max = 2): string {
  const s = ethers.formatUnits(v, decimals);
  const [whole, frac = ""] = s.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const trimmed = frac.slice(0, max).replace(/0+$/, "");
  return trimmed ? `${grouped}.${trimmed}` : grouped;
}

export function ago(tsSeconds: number): string {
  const d = Math.max(0, Math.floor(Date.now() / 1000) - tsSeconds);
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}
